import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronUp, ChevronDown, GripVertical, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * useReorderable — hook that wraps a list of admin items (products, trips, courses)
 * and exposes drag-and-drop + up/down handlers + a save action.
 *
 * The hook is purely client-side reordering until `commit()` is called, which
 * POSTs the new id sequence to the backend.
 *
 * @param {Array} items        original list (any objects with `id`)
 * @param {string} endpoint    full path under /api, e.g. "/admin/products/reorder"
 * @param {string} token       admin auth token
 * @param {Function} onSaved   optional callback (e.g. to refetch)
 */
export const useReorderable = (items, endpoint, token, onSaved) => {
  const [order, setOrder] = useState(items.map((it) => it.id));
  const [saving, setSaving] = useState(false);
  const dragId = useRef(null);

  // keep local order in sync when parent refetches with new items
  const reset = (newItems) => setOrder(newItems.map((it) => it.id));

  const moveBy = (id, delta) => {
    setOrder((prev) => {
      const i = prev.indexOf(id);
      if (i === -1) return prev;
      const j = i + delta;
      if (j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const handleDragStart = (id) => { dragId.current = id; };
  const handleDragOver = (e, overId) => {
    e.preventDefault();
    const dragging = dragId.current;
    if (!dragging || dragging === overId) return;
    setOrder((prev) => {
      const from = prev.indexOf(dragging);
      const to = prev.indexOf(overId);
      if (from === -1 || to === -1) return prev;
      const copy = [...prev];
      copy.splice(from, 1);
      copy.splice(to, 0, dragging);
      return copy;
    });
  };
  const handleDragEnd = () => { dragId.current = null; };

  const dirty = order.length !== items.length || order.some((id, i) => items[i]?.id !== id);

  const commit = async () => {
    if (!dirty) {
      toast.info('Nothing to save — order unchanged');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API_URL}${endpoint}`, { ids: order }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Order saved');
      if (typeof onSaved === 'function') onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  // Reorder a shallow copy of items based on the current id sequence
  const orderedItems = order
    .map((id) => items.find((it) => it.id === id))
    .filter(Boolean);

  return {
    orderedItems,
    moveUp: (id) => moveBy(id, -1),
    moveDown: (id) => moveBy(id, +1),
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    commit,
    dirty,
    saving,
    reset,
  };
};

/**
 * ReorderHandle — the drag handle + up/down arrow cluster.
 * Drop this inside a list row, alongside the row label.
 */
export const ReorderHandle = ({ id, onUp, onDown, onDragStart, onDragOver, onDragEnd, isFirst, isLast, testIdPrefix = 'reorder' }) => (
  <div
    className="flex items-center gap-0.5 select-none"
    draggable
    onDragStart={() => onDragStart(id)}
    onDragOver={(e) => onDragOver(e, id)}
    onDragEnd={onDragEnd}
    data-testid={`${testIdPrefix}-handle-${id}`}
    title="Drag to reorder, or use up/down arrows"
  >
    <GripVertical className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
    <button
      type="button"
      onClick={() => onUp(id)}
      disabled={isFirst}
      className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
      aria-label="Move up"
      data-testid={`${testIdPrefix}-up-${id}`}
    >
      <ChevronUp className="w-4 h-4" />
    </button>
    <button
      type="button"
      onClick={() => onDown(id)}
      disabled={isLast}
      className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
      aria-label="Move down"
      data-testid={`${testIdPrefix}-down-${id}`}
    >
      <ChevronDown className="w-4 h-4" />
    </button>
  </div>
);

/**
 * ReorderSaveBar — sticky "Save order" CTA shown when there are pending changes.
 */
export const ReorderSaveBar = ({ dirty, saving, onSave, label = 'order' }) => {
  if (!dirty) return null;
  return (
    <div className="sticky top-0 z-20 -mx-4 px-4 py-2 mb-3 bg-amber-50 border-y border-amber-200 flex items-center justify-between" data-testid="reorder-savebar">
      <span className="text-sm text-amber-900">
        You've changed the {label}. Don't forget to save.
      </span>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="px-3 py-1 text-sm bg-[#FF6B35] text-white rounded hover:bg-[#FF6B35]/90 disabled:opacity-50 flex items-center gap-2"
        data-testid="reorder-save-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Save order
      </button>
    </div>
  );
};
